import React from "react";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Ticket Master (#276) — pretty, indented JSON/value renderer.
//
// Objects/arrays render as an indented key/value tree (#4). Each pair is a
// block with a HANGING INDENT (padding-left + negative text-indent): the key
// starts at the left margin, and when the value wraps, its continuation lines
// indent past the key so keys stay scannable down the left edge (#6). Strings
// that are actually JSON (e.g. TurboQ payloads) are parsed and shown as a tree.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const HANG = 16; // px the wrapped value indents past the key
const STEP = 14; // px per nesting depth

function tryParse(s) {
  if (typeof s !== "string") return undefined;
  const t = s.trim();
  if (!(t.startsWith("{") || t.startsWith("["))) return undefined;
  try {
    return JSON.parse(t);
  } catch {
    return undefined;
  }
}

function scalarColor(value) {
  if (typeof value === "number") return "#4aa3df";
  if (typeof value === "boolean") return "#c586c0";
  if (value === null) return "#999";
  return undefined;
}

function entriesOf(v) {
  return Array.isArray(v) ? v.map((x, i) => [String(i), x]) : Object.entries(v);
}

const isPrimitive = (x) => x === null || typeof x !== "object";
const scalarText = (x) => (x === null ? "null" : typeof x === "string" ? x : String(x));

function Node({ k, value, depth }) {
  const parsed = tryParse(value);
  const v = parsed !== undefined ? parsed : value;
  const isObj = v && typeof v === "object";
  const pad = depth * STEP;

  // An array of scalars renders as ONE compact, wrapped list (with a count) —
  // not one numbered line per element (e.g. blackoutDates: 46 dates).
  if (Array.isArray(v) && v.every(isPrimitive)) {
    return (
      <div className="wrap-break-word" style={{ paddingLeft: pad + HANG, textIndent: -HANG, whiteSpace: "pre-wrap" }}>
        {k != null ? <span className="font-semibold opacity-80">{k} </span> : null}
        <span className="opacity-50">[{v.length}]</span>
        {v.length ? <span className="font-semibold opacity-80">: </span> : null}
        <span>{v.map(scalarText).join(", ")}</span>
      </div>
    );
  }

  if (!isObj) {
    const text = v === null ? "null" : typeof v === "string" ? v : String(v);
    return (
      <div
        className="wrap-break-word"
        style={{ paddingLeft: pad + HANG, textIndent: -HANG, whiteSpace: "pre-wrap" }}
      >
        {k != null ? <span className="font-semibold opacity-80">{k}: </span> : null}
        <span style={{ color: scalarColor(v) }}>{text}</span>
      </div>
    );
  }

  // Object/array: key header line, then children indented one level deeper.
  return (
    <>
      {k != null ? (
        <div style={{ paddingLeft: pad + HANG, textIndent: -HANG }}>
          <span className="font-semibold opacity-80">{k}:</span>
        </div>
      ) : null}
      {entriesOf(v).map(([ck, cv]) => (
        <Node key={ck} k={ck} value={cv} depth={depth + 1} />
      ))}
    </>
  );
}

/** @param {any} value  object | array | JSON string | scalar */
function PrettyJson({ value }) {
  const parsed = tryParse(value);
  const v = parsed !== undefined ? parsed : value;

  if (v && typeof v === "object") {
    return (
      <div className="text-[11px] font-mono leading-snug">
        {entriesOf(v).map(([k, cv]) => (
          <Node key={k} k={k} value={cv} depth={0} />
        ))}
      </div>
    );
  }
  return (
    <div className="text-[11px] font-mono leading-snug wrap-break-word" style={{ whiteSpace: "pre-wrap" }}>
      {v == null || v === "" ? "—" : String(v)}
    </div>
  );
}

export default PrettyJson;
